import { Permission, permissionSet } from '@all-hands-hq/plex-graphql-schema';
import { Button, ButtonGroup } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { Fragment, useEffect, useState } from 'react';

import {
  QuestionDetailsFragment,
  QuestionStatus,
  useGetQuestionDetailsQuery,
  useUpdateFactMutation,
} from '../../../../graphql/gen/types';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { canChangeAnswerApprovalStatus } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { DialogActions } from '../DialogActions';
import { useDialogState } from '../useDialogState';
import { FactDialogContent, State } from './FactDialogContent';

type Props = {
  factId: string;
};

export function UpdateFactDialogContent({ factId }: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();

  const [state, setState] = useState<State | null>(null);
  // TODO: error handling
  const { data, loading } = useGetQuestionDetailsQuery({ variables: { questionId: factId } });
  // TODO: error handling
  const [updateFact, { loading: updateLoading }] = useUpdateFactMutation();

  const question: QuestionDetailsFragment | null | undefined = data?.loggedIn?.questionById;

  useEffect(() => {
    if (!loading && question != null) {
      const plexRelation = question.plex.currentUserRelation?.state;
      const canPublishAnswer =
        plexRelation != null &&
        canChangeAnswerApprovalStatus({ plexRelation }) &&
        permissionSet(plexRelation).has(Permission.CHANGE_QUESTION_PUBLISH_STATUS);

      const answer = canPublishAnswer
        ? question.acceptedAnswer != null
          ? {
              body: SlateEditorValue.from(question.acceptedAnswer.body),
              entities: { expanded_link: question.acceptedAnswer.links, mention: question.acceptedAnswer.mentions },
              primaryLinkId: question.acceptedAnswer.primaryLink?.id,
            }
          : {
              body: SlateEditorValue.new(),
              entities: { expanded_link: [], mention: [] },
              primaryLinkId: null,
            }
        : undefined;

      setState({
        text: question.text,
        labelIds: question.labels.map((label) => label.id),
        boardId: question.boardId || '',
        answer,
      });
    }
  }, [factId, loading, question]);

  const handleClose = (): void => {
    setDialogState(null);
  };

  const handleSave = ({ changeDraftToInternal }: { changeDraftToInternal: boolean }) => (): void => {
    if (state != null) {
      const { answer, ...rest } = state;
      const approvedAnswer =
        answer != null
          ? {
              body: answer.body.value,
              primaryLinkId: answer.primaryLinkId,
            }
          : undefined;
      updateFact({
        variables: { question: { id: factId, changeDraftToInternal, answer: approvedAnswer, ...rest } },
        update: (cache, { data }) => {
          if (question != null && data?.updateQuestion != null) {
            if (question.boardId !== data.updateQuestion.boardId) {
              cache.evict({
                id: cache.identify({ __typename: 'Board', id: question.boardId }),
                fieldName: 'faqCount',
              });
              cache.evict({
                id: cache.identify({ __typename: 'Board', id: data.updateQuestion.boardId }),
                fieldName: 'faqCount',
              });
            }
          }
        },
      }).then(({ data }) => {
        if (data?.updateQuestion != null) {
          handleClose();
          logAnalytics({ name: 'faq updated', details: { plexId: data?.updateQuestion?.plex.id, itemId: factId } });
        }
      });
    }
  };

  const handleChange = (state: State): void => {
    setState(state);
  };

  const plexData = data?.loggedIn?.questionById?.plex;

  if (!state || !plexData || !question) {
    return <Skeleton variant="text" />;
  }

  return (
    <Fragment>
      <FactDialogContent
        type={question.type}
        value={state}
        onChange={handleChange}
        title={'Update FAQ'}
        plexName={plexData.name}
        plexId={plexData.id}
        plexColor={plexData.color}
        role={plexData.currentUserRelation.state}
      />

      <DialogActions
        cta={
          question?.status === QuestionStatus.Draft ? (
            <ButtonGroup disabled={loading || updateLoading}>
              <Button onClick={handleSave({ changeDraftToInternal: false })} variant={'contained'} color={'primary'}>
                Update draft
              </Button>
              <MoreOptionsMenu
                id={'update'}
                variant={'contained'}
                color={'primary'}
                size={'small'}
                options={[
                  {
                    label: 'Post',
                    onClick: handleSave({ changeDraftToInternal: true }),
                  },
                ]}
                Button={Button}
                menuProps={{
                  marginThreshold: 0,
                  anchorOrigin: { horizontal: 'right', vertical: 'top' },
                  transformOrigin: { horizontal: 'right', vertical: 'bottom' },
                }}
              />
            </ButtonGroup>
          ) : (
            <Button
              onClick={handleSave({ changeDraftToInternal: false })}
              variant={'contained'}
              color={'primary'}
              disabled={loading || updateLoading}
            >
              Update
            </Button>
          )
        }
      />
    </Fragment>
  );
}
